import { useRef, type ReactNode } from "react";
import { useFrozenBand, useStuck } from "./sticky";

/**
 * Every page starts here: one h1 (the thing focus lands on after a route
 * change — layouts/RouteFocus), an optional eyebrow above it, one line
 * under it, actions on the right that wrap under it on a phone, and — for
 * a list page — its filters in `toolbar`. A div, not a <header>: the page
 * has one banner, the shell's.
 *
 * The band stays in view while the page scrolls (owner, 2026-09-21;
 * gairm/lms#215): sticky at the top, under the shell's row with the round ?
 * (--shell-bar, sticky.ts), painting the canvas so rows do not
 * show through it, bleeding into <main>'s side padding (--page-inset-x,
 * set by AppLayout) and — when it is the first thing on the page — its top
 * padding, so nothing shows around it either; a bottom edge only once rows
 * pass beneath. Its height is --page-bar, which a table's filter row and
 * column titles stack under (sticky.ts). Frozen only where there is room,
 * measured (useFrozenBand): on a phone, at 200 % zoom, in a short window,
 * or wherever the band itself is taller than a third of the window (the
 * audit trail's filters wrapped, x-large text) a band that never scrolls
 * away would cover what focus moves to (WCAG 2.4.11), so there it scrolls
 * with the page and Back to top does the work.
 *
 * `align="center"` is for a page that is one narrow column rather than a
 * page of content — sign-in, where the eye must land in the same place
 * before and after the hand-off to managed login, which centres its own
 * form (infrastructure/branding/managed-login.json). Everything else
 * reads left, and `actions` belongs to a page of content, not a column. A
 * centred page has nothing to scroll under it, so it never freezes.
 */
export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
  toolbar,
  align = "start",
  sticky = align !== "center",
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** A list page's filters, frozen with the title. */
  toolbar?: ReactNode;
  align?: "start" | "center";
  /** `false` for a page with nothing to scroll under it. */
  sticky?: boolean;
}) {
  const centred = align === "center";
  const ref = useRef<HTMLDivElement>(null);
  const stuck = useStuck(ref) && sticky;
  const frozen = useFrozenBand(ref, "page-bar", "root", sticky);
  const band = sticky
    ? `bg-canvas ${frozen ? "sticky top-[var(--shell-bar,0px)] z-20" : ""} first:-mt-4 first:pt-4 [main>&]:-mx-[var(--page-inset-x,0px)] [main>&]:px-[var(--page-inset-x,0px)] [main>&]:first:-mt-8 [main>&]:first:pt-8 ${stuck ? "shadow-[0_1px_0_0_var(--color-line)]" : ""}`
    : "";
  return (
    <div ref={ref} data-testid="page-header" data-frozen={frozen ? "true" : undefined} data-stuck={stuck ? "true" : undefined} className={`mb-4 pb-4 ${band}`}>
      <div className={`flex flex-wrap items-end gap-4 ${centred ? "justify-center text-center" : "justify-between"}`}>
        <div className="min-w-0">
          {eyebrow && <div className={`mb-2 flex flex-wrap items-center gap-2 text-sm text-muted ${centred ? "justify-center" : ""}`}>{eyebrow}</div>}
          <h1 tabIndex={-1} className="text-2xl font-semibold tracking-tight text-ink sm:text-3xl">
            {title}
          </h1>
          {subtitle && <p className={`mt-2 max-w-prose text-base text-muted ${centred ? "mx-auto" : ""}`}>{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
      </div>
      {toolbar && <div className="mt-4 min-w-0">{toolbar}</div>}
    </div>
  );
}
